import { createSlice, Slice } from '@reduxjs/toolkit';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import { RootStateOrAny } from 'react-redux';
import { IAdminData } from './../../../app/context/types';
export const AUTH_KEY_IN_STORE = 'auth';

interface AuthenticatState {
	adminUserData: IAdminData | null;
	isAuthenticated: boolean;
	initAdminUserIsLoading: boolean;
	isLoading: boolean;
	invalidCredentialsError: boolean;
	resetPasswordRequest: IReqStatus;
	resetPassword: IReqStatus;
	verifyResetPasswordLink: {
		isLoading: boolean;
		status: IReqStatus;
	};
}

const initState: AuthenticatState = {
	adminUserData: null,
	isAuthenticated: false,
	isLoading: false,
	initAdminUserIsLoading: true,
	invalidCredentialsError: false,
	resetPasswordRequest: {
		success: false,
		error: false,
	},
	verifyResetPasswordLink: {
		isLoading: false,
		status: { success: false, error: false },
	},
	resetPassword: {
		success: false,
		error: false,
	},
};

const authSlice: Slice = createSlice({
	name: AUTH_KEY_IN_STORE,
	initialState: initState,
	reducers: {
		initAdminUserData: state => {
			state.initAdminUserIsLoading = true;
		},
		resetState: state => {
			return { ...initState, initAdminUserIsLoading: false };
		},
		setAdminUserData: (state, action) => {
			if (action.payload) {
				state.adminUserData = action.payload;
				state.isAuthenticated = true;
			} else {
				state.isAuthenticated = false;
			}
			state.initAdminUserIsLoading = false;
		},
		login: state => {
			state.isLoading = true;
			state.invalidCredentialsError = false;
		},
		loginSuccess: (state: RootStateOrAny, action) => {
			state.isAuthenticated = true;
			state.adminUserData = action.payload;
			state.isLoading = false;
		},
		loginFailure: state => {
			state.invalidCredentialsError = true;
			state.isLoading = false;
		},
		logout: state => {
			return { ...initState, initAdminUserIsLoading: false };
		},
		resetPasswordRequest: state => {
			state.resetPasswordRequest = { success: false, error: false };
			state.isLoading = true;
		},
		resetPasswordRequestSuccess: state => {
			state.resetPasswordRequest.success = true;
			state.isLoading = false;
		},
		resetPasswordRequestFail: state => {
			state.resetPasswordRequest.success = false;
			state.resetPasswordRequest.error = true;

			state.isLoading = false;
		},
		resetResetPasswordRequestData: state => {
			state.resetPasswordRequest = {
				success: false,
				error: false,
			};
			state.isLoading = false;
		},
		resetPassword: state => {
			state.resetPassword = { success: false, error: false };
			state.isLoading = true;
		},
		resetPasswordSuccess: state => {
			state.resetPassword.success = true;
			state.isLoading = false;
		},
		resetPasswordFail: state => {
			state.resetPassword.success = false;
			state.resetPassword.error = true;
			state.isLoading = false;
		},
		verifyResetPasswordLink: (state: AuthenticatState) => {
			state.verifyResetPasswordLink = {
				isLoading: true,
				status: {
					success: false,
					error: false,
				},
			};
		},
		verifyResetPasswordLinkSuccess: (state: AuthenticatState) => {
			state.verifyResetPasswordLink = {
				isLoading: false,
				status: {
					success: true,
					error: false,
				},
			};
		},
		verifyResetPasswordLinkFail: (state: AuthenticatState) => {
			state.verifyResetPasswordLink = {
				isLoading: false,
				status: {
					success: false,
					error: true,
				},
			};
		},
	},
});

export const {
	login,
	loginSuccess,
	loginFailure,
	logout,
	initAdminUserData,
	setAdminUserData,
	resetPasswordRequest,
	resetPasswordRequestSuccess,
	resetPasswordRequestFail,
	resetPassword,
	resetPasswordSuccess,
	resetPasswordFail,
	verifyResetPasswordLink,
	verifyResetPasswordLinkSuccess,
	verifyResetPasswordLinkFail,
	resetResetPasswordRequestData,
	resetState,
} = authSlice.actions;

export const Selectors = {
	adminUserData: (state: RootStateOrAny): IAdminData =>
		state[AUTH_KEY_IN_STORE].adminUserData,
	isLoading: (state: RootStateOrAny) => state[AUTH_KEY_IN_STORE].isLoading,
	isAuthenticated: (state: RootStateOrAny) =>
		state[AUTH_KEY_IN_STORE].isAuthenticated,
	invalidCredentialsError: (state: RootStateOrAny) =>
		state[AUTH_KEY_IN_STORE].invalidCredentialsError,
	resetPasswordRequest: (state: RootStateOrAny) =>
		state[AUTH_KEY_IN_STORE].resetPasswordRequest,

	resetPassword: (state: RootStateOrAny) =>
		state[AUTH_KEY_IN_STORE].resetPassword,

	initAdminUserIsLoading: (state: RootStateOrAny) =>
		state[AUTH_KEY_IN_STORE].initAdminUserIsLoading,
	verifyResetPasswordLink: (state: RootStateOrAny) =>
		state[AUTH_KEY_IN_STORE].verifyResetPasswordLink,
};

export const authReducer = authSlice.reducer;

export default authSlice;
